/**
 * AriaParticleFace — riusabile estratto da AriaPromo.jsx.
 *
 * Particelle Three.js + GLSL shaders che formano un volto femminile.
 * Stato sempre "steady" (post-emergenza, respirante), NO timeline.
 *
 * Usato in:
 *   - /aria/demo/cinematic  (sopra la chat in layer)
 *   - future campagne / banner inline
 *
 * Props:
 *   size      "sm" (240px) | "md" (320px) | "lg" (440px) | "fill" (riempie il parent)
 *   className passa classi sul container (gestisci tu posizione)
 *
 * Il container è position: relative; il canvas è absolute inset:0.
 */
import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const FACE_SRC = "/aria-face.png";

const SIZE_PX = {
    sm: 240,
    md: 320,
    lg: 440,
};

export default function AriaParticleFace({ size = "md", className = "" }) {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const aria = createAriaScene(container);

        // carica face image + sampling
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const points = sampleFacePoints(img, 9000);
            aria.setFacePoints(points);
        };
        img.src = FACE_SRC;

        // animazione loop continuo
        let raf;
        const start = performance.now();
        const loop = () => {
            const t = (performance.now() - start) / 1000;
            aria.update(t);
            aria.render();
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        const ro = new ResizeObserver(() => aria.resize());
        ro.observe(container);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            aria.dispose();
        };
    }, []);

    const sizeStyle = size === "fill"
        ? { width: "100%", height: "100%" }
        : { width: SIZE_PX[size] || SIZE_PX.md, height: (SIZE_PX[size] || SIZE_PX.md) * 1.22 };

    return (
        <div
            ref={containerRef}
            data-testid="aria-particle-face"
            className={className}
            style={{ position: "relative", ...sizeStyle }}
        />
    );
}

/* ============================================================
 * Three.js scene factory (semplificato dalla versione promo)
 * ============================================================ */
function createAriaScene(container) {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 4.2);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    container.appendChild(renderer.domElement);
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";

    const uniforms = {
        uTime: { value: 0 },
        uEmerge: { value: 1 },    // sempre emerso
        uOpacity: { value: 1 },
        uPixelRatio: { value: renderer.getPixelRatio() },
    };

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(0), 3));

    const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geom, material);
    const group = new THREE.Group();
    group.add(points);
    scene.add(group);

    function resize() {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w === 0 || h === 0) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
    }
    resize();

    function setFacePoints(cloud) {
        geom.setAttribute("position", new THREE.BufferAttribute(cloud.positions, 3));
        geom.setAttribute("aRandom", new THREE.BufferAttribute(cloud.random, 1));
        geom.setAttribute("aIndex", new THREE.BufferAttribute(cloud.idx, 1));
        geom.setAttribute("aHair", new THREE.BufferAttribute(cloud.hair, 1));
        geom.setAttribute("aHomeZ", new THREE.BufferAttribute(cloud.homeZ, 1));
        geom.computeBoundingSphere();
    }

    function update(t) {
        uniforms.uTime.value = t;

        // respiro ampio costante
        const breath = 1 + Math.sin(t * 1.0) * 0.022;
        const floatX = Math.sin(t * 0.6) * 0.04;
        const floatY = Math.sin(t * 0.4 + 1.3) * 0.06;
        const floatZ = Math.sin(t * 0.5) * 0.03;
        const rotY = Math.sin(t * 0.22) * (6 * Math.PI / 180);   // leggera testa che oscilla

        group.scale.setScalar(breath);
        group.position.set(floatX, floatY, floatZ);
        group.rotation.y = rotY;

        uniforms.uOpacity.value = 0.82 + Math.sin(t * 0.9) * 0.10;
    }

    function render() { renderer.render(scene, camera); }

    function dispose() {
        geom.dispose();
        material.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    }

    return { setFacePoints, update, render, resize, dispose };
}

/* ============================================================
 * Face Sampling (uguale al promo)
 * ============================================================ */
function sampleFacePoints(img, N) {
    const SAMPLE_W = 280;
    const SAMPLE_H = Math.round(SAMPLE_W * img.height / img.width);
    const off = document.createElement("canvas");
    off.width = SAMPLE_W; off.height = SAMPLE_H;
    const octx = off.getContext("2d", { willReadFrequently: true });
    octx.drawImage(img, 0, 0, SAMPLE_W, SAMPLE_H);
    const data = octx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;

    const worldH = 2.8;
    const worldW = worldH * (SAMPLE_W / SAMPLE_H);

    const positions = new Float32Array(N * 3);
    const random = new Float32Array(N);
    const idx = new Float32Array(N);
    const hair = new Float32Array(N);
    const homeZ = new Float32Array(N);

    let placed = 0;
    let attempts = 0;
    const maxAttempts = N * 80;
    while (placed < N && attempts < maxAttempts) {
        attempts++;
        const px = Math.floor(Math.random() * SAMPLE_W);
        const py = Math.floor(Math.random() * SAMPLE_H);
        const i = (py * SAMPLE_W + px) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
        if (lum < 0.16) continue;
        if (Math.random() > Math.pow(lum, 0.95)) continue;

        const nx = (px / SAMPLE_W - 0.5) * worldW;
        const ny = -(py / SAMPLE_H - 0.5) * worldH;

        const dx = (px / SAMPLE_W) - 0.5;
        const dy = (py / SAMPLE_H) - 0.40;
        const distCenter = Math.hypot(dx, dy * 1.2);
        const z = Math.cos(Math.min(1, distCenter * 2.4) * Math.PI / 2) * 0.40 - 0.10;
        const zJitter = (Math.random() - 0.5) * 0.06;

        const ovalDist = Math.hypot(dx / 0.20, (dy + 0.05) / 0.30);
        const isHair = ovalDist > 1 ? 1 : 0;

        positions[placed * 3] = nx;
        positions[placed * 3 + 1] = ny;
        positions[placed * 3 + 2] = z + zJitter;
        random[placed] = Math.random();
        idx[placed] = Math.min(1, distCenter * 1.5);
        hair[placed] = isHair;
        homeZ[placed] = z + zJitter;
        placed++;
    }

    return { positions, random, idx, hair, homeZ };
}

/* ============================================================
 * GLSL Shaders (identici al promo, in steady-state)
 * ============================================================ */
const vertexShader = /* glsl */ `
attribute float aRandom;
attribute float aIndex;
attribute float aHair;
attribute float aHomeZ;

uniform float uTime;
uniform float uEmerge;
uniform float uOpacity;
uniform float uPixelRatio;

varying float vAlpha;
varying float vFlicker;

void main() {
    vec3 pos = position;

    // Floating drift
    float depthFactor = (aHomeZ + 0.5);
    pos.x += sin(uTime * 0.7 + aRandom * 6.28) * 0.012 * depthFactor;
    pos.y += sin(uTime * 0.5 + aRandom * 9.31) * 0.018 * depthFactor;
    pos.z += sin(uTime * 0.6 + aRandom * 13.0) * 0.010 * depthFactor;

    // Hair wind
    if (aHair > 0.5) {
        pos.x += sin(uTime * 0.9 + pos.y * 2.0 + aRandom * 4.0) * 0.06;
        pos.y -= 0.005 * sin(uTime * 0.4 + aRandom * 3.0);
    }

    vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPos;

    float baseSize = mix(0.40, 0.95, depthFactor);
    if (aHair > 0.5) baseSize *= 0.78;
    gl_PointSize = baseSize * uPixelRatio * (16.0 / -mvPos.z);

    vFlicker = 0.7 + 0.30 * sin(uTime * 4.0 + aRandom * 28.0);
    vAlpha = uOpacity * (0.42 + 0.55 * uEmerge);
}
`;

const fragmentShader = /* glsl */ `
precision mediump float;
varying float vAlpha;
varying float vFlicker;

void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.0, d);
    a = pow(a, 2.4);
    vec3 col = vec3(1.0) * vFlicker;
    gl_FragColor = vec4(col, a * vAlpha);
}
`;
